import { useForm } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { HintText, InputBase, Label, TextField } from "@/components/ui/input"
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from "@/components/ui/modal"
import { NativeSelect } from "@/components/ui/select"
import { TextArea } from "@/components/ui/textarea"
import { toFieldError } from "@/lib/utils"
import { adminSchoolTuningsPath } from "@/routes"

import type { ScopeOption, TuningKind } from "../types"

const KINDS: { value: TuningKind; label: string; hint: string }[] = [
  {
    value: "snooze",
    label: "Snooze until a date",
    hint: "Off the list until that day; back on it that morning.",
  },
  {
    value: "pin",
    label: "Pin to the top",
    hint: "First on the list until unpinned.",
  },
  {
    value: "dismiss",
    label: "Dismiss",
    hint: "Off every list, with a reason.",
  },
  {
    value: "not_my_region",
    label: "Not my region",
    hint: "Off one scope's list, with a reason.",
  },
]

/** The keys the controller can send back; the ones with a field of their own render beside it. */
const FIELD_ERRORS = ["kind", "until_on", "reason", "call_list_scope_id"]

interface FormData {
  kind: TuningKind
  until_on: string
  reason: string
  call_list_scope_id: string
  [key: string]: string
}

/** The first day a snooze can end: tomorrow, in the browser's calendar. */
const tomorrow = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

/**
 * The app-level tunes: snooze, pin, dismiss, not my region. Salesforce-bound
 * tunes (booking contact, do not call, staff changed) are Phase 4 and are
 * not offered here. From the list, "not my region" means the list's scope;
 * from a card with no list behind it, the caller offers the scopes and the
 * school's own is picked first.
 */
export function TuneModal({
  schoolId,
  schoolName,
  scopeId,
  scopes = [],
  defaultScopeId = null,
  returnToScope,
  trigger,
}: {
  schoolId: number
  schoolName: string
  scopeId: number | null
  scopes?: ScopeOption[]
  defaultScopeId?: number | null
  returnToScope?: number | null
  trigger?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const form = useForm<FormData>({
    kind: "snooze",
    until_on: "",
    reason: "",
    call_list_scope_id: String(
      scopeId ?? defaultScopeId ?? scopes[0]?.id ?? "",
    ),
  })
  const kind = KINDS.find((k) => k.value === form.data.kind) ?? KINDS[0]
  const pickScope = scopeId === null && form.data.kind === "not_my_region"
  const otherErrors = Object.entries(form.errors).filter(
    ([key]) => !FIELD_ERRORS.includes(key),
  )

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({
      tuning: {
        kind: data.kind,
        until_on: data.kind === "snooze" ? data.until_on : null,
        reason: data.reason || null,
        call_list_scope_id:
          data.kind === "not_my_region"
            ? (scopeId ?? (Number(data.call_list_scope_id) || null))
            : null,
      },
      return_to_scope: returnToScope ?? null,
    }))
    form.post(adminSchoolTuningsPath(schoolId), {
      preserveScroll: true,
      onSuccess: () => {
        setOpen(false)
        form.reset()
      },
    })
  }

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {trigger ?? (
        <Button size="sm" color="secondary">
          Tune
        </Button>
      )}
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog className="bg-primary mx-auto mt-20 flex w-full max-w-md flex-col gap-5 rounded-xl p-6 shadow-xl md:mt-0">
            <div className="flex flex-col gap-1">
              <h3 className="text-primary text-lg font-semibold">
                Tune {schoolName}
              </h3>
              <p className="text-tertiary text-sm">
                Changes tomorrow's list, for every coordinator. Nothing here
                touches Salesforce.
              </p>
            </div>
            <form onSubmit={submit} className="flex flex-col gap-4">
              <NativeSelect
                label="What"
                value={form.data.kind}
                onChange={(e) =>
                  form.setData("kind", e.target.value as TuningKind)
                }
                options={KINDS.map((k) => ({ label: k.label, value: k.value }))}
              />
              {form.errors.kind ? (
                <HintText isInvalid className="-mt-2">
                  {form.errors.kind}
                </HintText>
              ) : (
                <p className="text-tertiary -mt-2 text-xs">{kind.hint}</p>
              )}
              {form.data.kind === "snooze" && (
                <TextField
                  type="date"
                  isRequired
                  value={form.data.until_on}
                  onChange={(value: string) => form.setData("until_on", value)}
                  isInvalid={!!form.errors.until_on}
                  error={toFieldError(form.errors.until_on)}
                >
                  <Label>Until</Label>
                  {/* min is a native date-input attribute the kit's props do not name; it reaches the input through the spread. */}
                  <InputBase size="sm" {...{ min: tomorrow() }} />
                </TextField>
              )}
              {pickScope && (
                <div className="flex flex-col gap-1.5">
                  <NativeSelect
                    label="Which list"
                    value={form.data.call_list_scope_id}
                    onChange={(e) =>
                      form.setData("call_list_scope_id", e.target.value)
                    }
                    options={scopes.map((s) => ({
                      label: s.label,
                      value: String(s.id),
                    }))}
                  />
                  {form.errors.call_list_scope_id && (
                    <HintText isInvalid>
                      {form.errors.call_list_scope_id}
                    </HintText>
                  )}
                </div>
              )}
              {form.data.kind !== "pin" && (
                <TextArea
                  label={
                    form.data.kind === "snooze" ? "Reason (optional)" : "Reason"
                  }
                  rows={3}
                  value={form.data.reason}
                  onChange={(value: string) => form.setData("reason", value)}
                  isInvalid={!!form.errors.reason}
                  hint={form.errors.reason}
                />
              )}
              {otherErrors.length > 0 && (
                <ul className="text-error-primary flex flex-col gap-1 text-sm">
                  {otherErrors.map(([key, message]) => (
                    <li key={key}>
                      {key === "base" ? "" : `${key.replace(/_/g, " ")} `}
                      {message}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  size="md"
                  color="secondary"
                  onPress={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  color="primary"
                  type="submit"
                  isLoading={form.processing}
                >
                  Save
                </Button>
              </div>
            </form>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  )
}
